import { makeAutoObservable, type IReactionDisposer, reaction } from 'mobx'
import { Box, Typography } from 'shared/ui'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import { ChatbotDisqualifyBehaviorStatus } from 'entities/Chatbot'
import { IntegrationKey } from 'entities/Integrations'
import { mergeFieldReplacer, MessageFieldStore } from 'widgets/MessageField'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { type ChatbotStore } from 'pages/chatbot'
import { ChatbotTooltip } from 'pages/chatbot/ui'

export class StepQualifyStore {
  private readonly _messageFieldStore: MessageFieldStore
  private _disposeMessage: IReactionDisposer | null = null
  private _disposeSettings: IReactionDisposer | null = null
  private _disposeError: IReactionDisposer | null = null
  private _isUpdatingFromSettings = false
  constructor(private _chatbotStore: ChatbotStore, private _entities: IEntitiesStore) {
    this._messageFieldStore = new MessageFieldStore(
      this._getMessageFieldStoreProps(),
      this._entities
    )

    makeAutoObservable(this)
  }

  private _getMessageFieldStoreProps = () => ({
    placeholder: (
      <>
        <Typography
          variant={'body-rg-regular'}
          ariaLabel={'Example 1'}
          color={'var(--content-neutral-primary,)'}
        >
          Example 1:
          <br /> If{' '}
          <span className='mergeField-wrap disabled'>
            <span className='mergeField-content mergeField-salesmessage'>First Name</span>
          </span>{' '}
          and{' '}
          <span className='mergeField-wrap disabled'>
            <span className='mergeField-content mergeField-salesmessage'>Last Name</span>
          </span>{' '}
          are known then qualify, otherwise - disqualify.
          <br />
        </Typography>
        <br />
        <Typography
          variant={'body-rg-regular'}
          ariaLabel={'Example 1'}
          color={'var(--content-neutral-primary,)'}
        >
          Example 2:
          <br /> If{' '}
          <span className='mergeField-wrap disabled'>
            <span className='mergeField-content mergeField-salesmessage'>Email</span>
          </span>{' '}
          is known then qualify, otherwise - disqualify.
        </Typography>
      </>
    ),
    noCounter: true,
    styles: {
      minHeight: '180px',
    },
    disableMergeFieldsFallback: true,
    resetOnUnmount: false,
    makeActions: () => {
      return [
        {
          iconButtonComponent: (
            <MergeFieldsIconAction
              onAddMergeField={(value) => this.onChooseMergeField(value)}
              isAllIntegration
              includeIntegrations={[IntegrationKey.hubspot]}
              excludeFieldsIntegrations={{
                [IntegrationKey.salesmessage]: ['number'],
              }}
            />
          ),
        },
      ]
    },
  })

  get chatbotStore() {
    return this._chatbotStore
  }

  get errors() {
    return this._chatbotStore.errors
  }

  get chatbot() {
    return this._chatbotStore.model
  }

  get messageFieldStore() {
    return this._messageFieldStore
  }

  get pathList() {
    return [
      {
        id: ChatbotDisqualifyBehaviorStatus.Stop,
        customLabel: (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Typography
              variant={'body-md-regular'}
              ariaLabel={'Politely conclude conversation'}
              color={'var(--content-primary-primary)'}
            >
              Politely conclude conversation
            </Typography>
            <ChatbotTooltip
              tooltipProps={{
                label: 'Agent creates and delivers a professional closing message',
                placement: 'top',
                sx: { whiteSpace: 'nowrap' },
                maxWidth: 'none',
              }}
            />
          </Box>
        ),
      },
      {
        id: ChatbotDisqualifyBehaviorStatus.Continue,
        customLabel: (
          <Typography
            variant={'body-md-regular'}
            ariaLabel={'Continue to success or fallback actions'}
          >
            Continue to success or fallback actions
          </Typography>
        ),
      },
    ]
  }

  onChooseMergeField = (field: MergeField) => {
    if (!this._messageFieldStore.addContent) return

    const fieldContent = mergeFieldReplacer(this._entities, field.template) + '&nbsp;'

    this._messageFieldStore.addContent(fieldContent)
  }

  initDefault = () => {
    this._initMessageField()
    this._reactionMessage()
    this._reactionSettings()
    this._reactionError()
  }

  dispose = () => {
    this._disposeMessage?.()
    this._disposeSettings?.()
    this._disposeError?.()
    this._messageFieldStore.reset()

    if (!this._chatbotStore.id) {
      this.chatbot.settings.setQualifyInstruction('')
    }
  }

  private _reactionMessage = () => {
    this._disposeMessage?.()
    this._disposeMessage = reaction(
      () => this._messageFieldStore.text,
      (value) => {
        if (this._isUpdatingFromSettings) return

        this.chatbot.settings.setQualifyInstruction(value)
        this.errors.removeError('settings.qualify_instruction')
      }
    )
  }

  private _reactionSettings = () => {
    this._disposeSettings?.()
    this._disposeSettings = reaction(
      () => this.chatbot.settings.qualifyInstruction,
      (value) => {
        const currentText = this._messageFieldStore.text

        if (currentText !== value) {
          this._isUpdatingFromSettings = true
          this.setMessageField(value)

          setTimeout(() => {
            this._isUpdatingFromSettings = false
          }, 0)
        }
      }
    )
  }

  private get _hasQualifyInstructionError() {
    return Boolean(
      this.errors.hasError('settings.qualify_instruction.value') ||
        this.errors.hasError('settings.qualify_instruction')
    )
  }

  private _reactionError = () => {
    this._disposeError?.()
    this._disposeError = reaction(
      () => this._hasQualifyInstructionError,
      (hasError) => {
        if (hasError) {
          this._messageFieldStore.setHighlight('error')
        } else {
          this._messageFieldStore.clearHighlight()
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  private _initMessageField = () => {
    const value = this.chatbot.settings.qualifyInstruction

    if (value) {
      this.setMessageField(value)
    }
  }

  setMessageField = (value: string, targetStore?: MessageFieldStore) => {
    const target = targetStore || this._messageFieldStore

    target.setMessageData({
      message: value,
      attachments: [],
      isReplace: true,
      replaceNewRow: false,
    })
  }
}
